<template>
  <div class="max-w-4xl mx-auto space-y-6">
    <!-- Wallet Selection Card -->
    <div v-if="`${authStore.user.is_active}` ==='1' && !dataStore.shouldHideWallet" class="bg-background-light rounded-lg shadow-sm">
      <div class="px-6 py-4 border-b border-background-lighter">
        <h2 class="text-xl font-semibold text-text-primary">Wallet Selection</h2>
        <p class="text-sm text-text-secondary mt-1">Choose your active wallet for transactions</p>
      </div>

      <div class="p-6">
        <div class="space-y-3">
          <!-- USD Wallet -->
          <div class="flex items-center p-3 border border-background-lighter rounded-lg hover:bg-background-lighter transition-colors">
            <div class="flex items-center">
              <input
                  type="radio"
                  id="wallet_usd"
                  name="active_wallet"
                  value="0"
                  v-model="selectedWallet"
                  @change="updateActiveWallet"
                  :disabled="isUpdatingWallet"
                  class="h-4 w-4 text-primary focus:ring-primary border-background-lighter"
              />
              <label for="wallet_usd" class="ml-3 flex items-center cursor-pointer">
                <span class="text-sm font-medium text-text-primary">US Dollar Wallet</span>
                <span :class="[
                  'ml-2 px-2 py-1 text-xs font-medium rounded-full',
                  selectedWallet === '0' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                ]">
                  {{ formatCurrency(walletBalances.usd || 0, 'USD') }}
                </span>
              </label>
            </div>
          </div>

          <!-- EUR Wallet -->
          <div class="flex items-center p-3 border border-background-lighter rounded-lg hover:bg-background-lighter transition-colors">
            <div class="flex items-center">
              <input
                  type="radio"
                  id="wallet_euro"
                  name="active_wallet"
                  value="1"
                  v-model="selectedWallet"
                  @change="updateActiveWallet"
                  :disabled="isUpdatingWallet"
                  class="h-4 w-4 text-primary focus:ring-primary border-background-lighter"
              />
              <label for="wallet_euro" class="ml-3 flex items-center cursor-pointer">
                <span class="text-sm font-medium text-text-primary">Euro Wallet</span>
                <span :class="[
                  'ml-2 px-2 py-1 text-xs font-medium rounded-full',
                  selectedWallet === '1' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                ]">
                  {{ formatCurrency(walletBalances.eur || 0, 'EUR') }}
                </span>
              </label>
            </div>
          </div>

          <!-- DZD Wallet -->
          <div class="flex items-center p-3 border border-background-lighter rounded-lg hover:bg-background-lighter transition-colors">
            <div class="flex items-center">
              <input
                  type="radio"
                  id="wallet_dzd"
                  name="active_wallet"
                  value="2"
                  v-model="selectedWallet"
                  @change="updateActiveWallet"
                  :disabled="isUpdatingWallet"
                  class="h-4 w-4 text-primary focus:ring-primary border-background-lighter"
              />
              <label for="wallet_dzd" class="ml-3 flex items-center cursor-pointer">
                <span class="text-sm font-medium text-text-primary">Algerian Dinar Wallet</span>
                <span :class="[
                  'ml-2 px-2 py-1 text-xs font-medium rounded-full',
                  selectedWallet === '2' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                ]">
                  {{ formatCurrency(walletBalances.dzd || 0, 'DZD') }}
                </span>
              </label>
            </div>
          </div>

          <!-- EGP Wallet -->
          <div class="flex items-center p-3 border border-background-lighter rounded-lg hover:bg-background-lighter transition-colors">
            <div class="flex items-center">
              <input
                  type="radio"
                  id="wallet_egp"
                  name="active_wallet"
                  value="3"
                  v-model="selectedWallet"
                  @change="updateActiveWallet"
                  :disabled="isUpdatingWallet"
                  class="h-4 w-4 text-primary focus:ring-primary border-background-lighter"
              />
              <label for="wallet_egp" class="ml-3 flex items-center cursor-pointer">
                <span class="text-sm font-medium text-text-primary">Egyptian Pound Wallet</span>
                <span :class="[
                  'ml-2 px-2 py-1 text-xs font-medium rounded-full',
                  selectedWallet === '3' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                ]">
                  {{ formatCurrency(walletBalances.egp || 0, 'EGP') }}
                </span>
              </label>
            </div>
          </div>

          <!-- Flexy Wallet (Read-only) -->
          <div v-if="authStore.user.is_flexy === '1'" class="flex items-center p-3 border border-background-lighter rounded-lg bg-background-lighter">
            <div class="flex items-center opacity-75">
              <input
                  type="radio"
                  id="wallet_flexy"
                  name="active_wallet"
                  disabled
                  class="h-4 w-4 text-text-secondary border-background-lighter"
              />
              <label for="wallet_flexy" class="ml-3 flex items-center">
                <span class="text-sm font-medium text-text-secondary">Flexy Wallet (Read-only)</span>
                <span class="ml-2 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                  {{ formatCurrency(walletBalances.flexy || 0, 'DZD') }}
                </span>
              </label>
            </div>
          </div>
        </div>

        <!-- Update Status -->
        <div v-if="isUpdatingWallet" class="mt-4 flex items-center justify-center">
          <div class="animate-spin h-5 w-5 text-primary">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <span class="ml-2 text-sm text-text-secondary">Updating wallet selection...</span>
        </div>
      </div>
    </div>

    <!-- Notification Preferences Card -->
    <div class="bg-background-light rounded-lg shadow-sm">
      <div class="px-6 py-4 border-b border-background-lighter">
        <h2 class="text-xl font-semibold text-text-primary">Notification Preferences</h2>
        <p class="text-sm text-text-secondary mt-1">Control how you receive notifications</p>
      </div>

      <div class="p-6 space-y-4">
        <!-- Email Notifications -->
        <div class="flex items-center justify-between p-4 border border-background-lighter rounded-lg hover:bg-background-lighter/50 transition-colors">
          <div class="flex items-start gap-4">
            <div class="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <svg class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <p class="text-sm font-medium text-text-primary">Email Notifications</p>
              <p class="text-xs text-text-secondary mt-0.5">Receive order updates and replies via email</p>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            :aria-checked="notificationsEmail"
            @click="toggleEmailNotifications"
            :disabled="isUpdatingNotif"
            :class="[
              'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
              notificationsEmail ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600',
              isUpdatingNotif ? 'opacity-70 cursor-wait' : 'cursor-pointer'
            ]"
          >
            <span
              :class="[
                'pointer-events-none inline-flex h-5 w-5 items-center justify-center rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                notificationsEmail ? 'translate-x-5' : 'translate-x-0'
              ]"
            >
              <svg v-if="isUpdatingNotif" class="w-3 h-3 text-text-secondary animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </span>
          </button>
        </div>

        <!-- Push Notifications (Desktop: realtime toggle, Mobile: push subscription) -->
        <div v-if="isMobile">
          <div class="flex items-center justify-between p-4 border border-background-lighter rounded-lg hover:bg-background-lighter/50 transition-colors">
            <div class="flex items-start gap-4">
              <div class="p-2 rounded-lg bg-orange-50 dark:bg-orange-900/20">
                <svg class="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div class="flex-1">
                <p class="text-sm font-medium text-text-primary">Push Notifications</p>
                <p class="text-xs text-text-secondary mt-0.5">Receive alerts even when the browser is in the background</p>
                <div class="mt-2" v-if="pushSub.supported">
                  <div v-if="pushSub.subscribed.value">
                    <span class="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Push notifications active
                    </span>
                  </div>
                  <div v-else-if="browserNotif.isSupported() && browserNotif.permission() === 'denied'">
                    <span class="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      Notifications blocked — allow in browser settings
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <button
              v-if="pushSub.supported"
              @click="togglePushSub"
              :disabled="pushSub.loading.value"
              :class="[
                'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                pushSub.subscribed.value
                  ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40'
                  : 'bg-primary text-background hover:bg-primary-dark'
              ]"
            >
              <svg v-if="pushSub.loading.value" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <template v-else>
                {{ pushSub.subscribed.value ? 'Unsubscribe' : 'Enable Notifications' }}
              </template>
            </button>
          </div>
          <p v-if="pushSub.error.value" class="px-4 text-xs text-red-500">{{ pushSub.error.value }}</p>
        </div>

        <div v-else class="flex items-center justify-between p-4 border border-background-lighter rounded-lg hover:bg-background-lighter/50 transition-colors">
          <div class="flex items-start gap-4">
            <div class="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20">
              <svg class="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <div class="flex-1">
              <p class="text-sm font-medium text-text-primary">Push Notifications</p>
              <p class="text-xs text-text-secondary mt-0.5">Receive real-time alerts in your browser</p>
              <div class="mt-2" v-if="browserNotif.isSupported()">
                <div v-if="browserNotif.permission() === 'granted'">
                  <span class="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Desktop notifications enabled
                  </span>
                </div>
                <div v-else-if="browserNotif.permission() === 'denied'">
                  <span class="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    Desktop notifications blocked
                  </span>
                </div>
                <button
                  v-else
                  @click="browserNotif.requestPermission()"
                  class="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary-dark transition-colors"
                >
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  Enable desktop notifications
                </button>
              </div>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            :aria-checked="notificationsRealtime"
            @click="toggleRealtimeNotifications"
            :disabled="isUpdatingNotif"
            :class="[
              'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
              notificationsRealtime ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600',
              isUpdatingNotif ? 'opacity-70 cursor-wait' : 'cursor-pointer'
            ]"
          >
            <span
              :class="[
                'pointer-events-none inline-flex h-5 w-5 items-center justify-center rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                notificationsRealtime ? 'translate-x-5' : 'translate-x-0'
              ]"
            >
              <svg v-if="isUpdatingNotif" class="w-3 h-3 text-text-secondary animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Profile Information Card -->
    <div class="bg-background-light rounded-lg shadow-sm">
      <div class="px-6 py-4 border-b border-background-lighter">
        <h2 class="text-xl font-semibold text-text-primary">Profile Information </h2>
        <p class="text-sm text-text-secondary mt-1">Update your personal information</p>
      </div>

      <div class="p-6">
        <form @submit.prevent="updateProfile" class="space-y-6">
          <!-- Profile Picture Display -->
          <div class="flex items-center space-x-6">
            <div class="w-20 h-20 bg-primary rounded-full flex items-center justify-center text-white text-2xl font-bold">
              {{ userInitials }}
            </div>
            <div>
              <h3 class="text-lg font-medium text-text-primary">{{ profileForm.name || 'User' }}</h3>
              <p class="text-text-secondary">{{ authStore.user?.email }}</p>
              <p v-if="authStore.user?.is_active" class="text-sm text-primary mt-1">
                Active Wallet: {{ getActiveWalletName() }}
              </p>
            </div>
          </div>

          <!-- Form Fields -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Full Name -->
            <div>
              <label for="name" class="block text-sm font-medium text-text-secondary mb-2">
                Full Name <span class="text-red-500">*</span>
              </label>
              <input
                  id="name"
                  v-model="profileForm.name"
                  type="text"
                  :disabled="isUpdatingProfile"
                  class="w-full px-3 py-2 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary disabled:bg-background disabled:cursor-not-allowed"
                  placeholder="Enter your full name"
                  required
              />
              <div v-if="errors.name" class="mt-1 text-sm text-red-600">
                {{ errors.name[0] }}
              </div>
            </div>

            <!-- Phone Number -->
            <div>
              <label for="phone" class="block text-sm font-medium text-text-secondary mb-2">
                Phone Number
              </label>
              <input
                  id="phone"
                  v-model="profileForm.phone"
                  type="tel"
                  :disabled="isUpdatingProfile"
                  class="w-full px-3 py-2 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary disabled:bg-background disabled:cursor-not-allowed"
                  placeholder="Enter your phone number"
              />
              <div v-if="errors.phone" class="mt-1 text-sm text-red-600">
                {{ errors.phone[0] }}
              </div>
            </div>

            <!-- Description -->
            <div>
              <label for="description" class="block text-sm font-medium text-text-secondary mb-2">
                Description
              </label>
              <textarea
                  id="description"
                  v-model="profileForm.description"
                  :disabled="isUpdatingProfile"
                  rows="3"
                  class="w-full px-3 py-2 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary disabled:bg-background disabled:cursor-not-allowed resize-none"
                  placeholder="Enter a description (appears on printed receipts)"
              />
              <div v-if="errors.description" class="mt-1 text-sm text-red-600">
                {{ errors.description[0] }}
              </div>
            </div>
          </div>

          <!-- Update Button -->
          <div class="flex justify-end pt-4">
            <button
                type="submit"
                :disabled="isUpdatingProfile || !hasProfileChanges"
                class="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <div v-if="isUpdatingProfile" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              {{ isUpdatingProfile ? 'Updating...' : 'Update Profile' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Change Password Card -->
    <div class="bg-background-light rounded-lg shadow-sm">
      <div class="px-6 py-4 border-b border-background-lighter">
        <h2 class="text-xl font-semibold text-text-primary">Change Password</h2>
        <p class="text-sm text-text-secondary mt-1">Update your password to keep your account secure</p>
      </div>

      <div class="p-6">
        <form @submit.prevent="updatePassword" class="space-y-6">
          <!-- Current Password -->
          <div>
            <label for="current_password" class="block text-sm font-medium text-text-secondary mb-2">
              Current Password <span class="text-red-500">*</span>
            </label>
            <input
                id="current_password"
                v-model="passwordForm.current_password"
                type="password"
                :disabled="isUpdatingPassword"
                class="w-full px-3 py-2 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary disabled:bg-background disabled:cursor-not-allowed"
                placeholder="Enter your current password"
                required
            />
            <div v-if="errors.current_password" class="mt-1 text-sm text-red-600">
              {{ errors.current_password[0] }}
            </div>
          </div>

          <!-- New Password -->
          <div>
            <label for="new_password" class="block text-sm font-medium text-text-secondary mb-2">
              New Password <span class="text-red-500">*</span>
            </label>
            <input
                id="new_password"
                v-model="passwordForm.new_password"
                type="password"
                :disabled="isUpdatingPassword"
                class="w-full px-3 py-2 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary disabled:bg-background disabled:cursor-not-allowed"
                placeholder="Enter your new password"
                required
                minlength="8"
            />
            <div v-if="errors.new_password" class="mt-1 text-sm text-red-600">
              {{ errors.new_password[0] }}
            </div>
          </div>

          <!-- Confirm New Password -->
          <div>
            <label for="new_password_confirmation" class="block text-sm font-medium text-text-secondary mb-2">
              Confirm New Password <span class="text-red-500">*</span>
            </label>
            <input
                id="new_password_confirmation"
                v-model="passwordForm.new_password_confirmation"
                type="password"
                :disabled="isUpdatingPassword"
                class="w-full px-3 py-2 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary disabled:bg-background disabled:cursor-not-allowed"
                placeholder="Confirm your new password"
                required
                minlength="8"
            />
            <div v-if="errors.new_password_confirmation" class="mt-1 text-sm text-red-600">
              {{ errors.new_password_confirmation[0] }}
            </div>
          </div>

          <!-- Update Button -->
          <div class="flex justify-end pt-4">
            <button
                type="submit"
                :disabled="isUpdatingPassword || !hasPasswordChanges"
                class="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <div v-if="isUpdatingPassword" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              {{ isUpdatingPassword ? 'Updating...' : 'Change Password' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useAuthStore } from '@/stores/auth.js';
import { useDataStore } from '@/stores/dataStore.js'

import api from '../../services/api';
import { updateUserPreferences } from '../../services/api';
import { useBrowserNotifications } from '@/composables/useBrowserNotifications';
import { usePushSubscription } from '@/composables/usePushSubscription';
import Swal from 'sweetalert2';

export default {
  name: 'Profile',
  setup() {
    const authStore = useAuthStore();
    const dataStore = useDataStore();
    const browserNotif = useBrowserNotifications();
    const pushSub = usePushSubscription();

    const profileForm = ref({
      name: '',
      phone: '',
      description: ''
    });

    const passwordForm = ref({
      current_password: '',
      new_password: '',
      new_password_confirmation: ''
    });

    // Wallet related states
    const selectedWallet = ref('0'); // Default to USD (is_da = 0)
    const isUpdatingWallet = ref(false);
    const walletBalances = ref({
      usd: 0,
      eur: 0,
      dzd: 0,
      egp: 0,
      flexy: 0
    });

    const isUpdatingProfile = ref(false);
    const isUpdatingPassword = ref(false);
    const errors = ref({});

    const notificationsEmail = ref(true);
    const notificationsRealtime = ref(true);
    const isUpdatingNotif = ref(false);
    const isMobile = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // Original data for comparison
    const originalProfileData = ref({});

    const userInitials = computed(() => {
      const name = profileForm.value.name || authStore.user?.name || 'U';
      return name.charAt(0).toUpperCase();
    });

    // Check if profile form has changes
    const hasProfileChanges = computed(() => {
      return profileForm.value.name !== originalProfileData.value.name ||
          profileForm.value.phone !== originalProfileData.value.phone ||
          profileForm.value.description !== originalProfileData.value.description;
    });

    // Check if password form has all required fields
    const hasPasswordChanges = computed(() => {
      return passwordForm.value.current_password &&
          passwordForm.value.new_password &&
          passwordForm.value.new_password_confirmation &&
          passwordForm.value.new_password === passwordForm.value.new_password_confirmation;
    });

    const formatCurrency = (amount, currency) => {
      const formattedAmount = parseFloat(amount || 0).toFixed(2);
      const symbols = {
        'USD': '$',
        'EUR': '€',
        'DZD': 'DZD',
        'EGP': 'EGP'
      };
      return `${formattedAmount} ${symbols[currency] || currency}`;
    };

    const getActiveWalletName = () => {
      const walletNames = {
        '0': 'US Dollar',
        '1': 'Euro',
        '2': 'Algerian Dinar',
        '3': 'Egyptian Pound'
      };
      return walletNames[selectedWallet.value] || 'Unknown';
    };

    const loadUserData = () => {
      if (authStore.user) {
        const userData = {
          name: authStore.user.name || '',
          phone: authStore.user.phone || '',
          description: authStore.user.description || ''
        };

        profileForm.value = { ...userData };
        originalProfileData.value = { ...userData };

        // Load notification preferences
        notificationsEmail.value = authStore.user.notifications_email !== false;
        notificationsRealtime.value = authStore.user.notifications_realtime !== false;

        // Load wallet data
        selectedWallet.value = String(authStore.user.is_da || 0);
        walletBalances.value = {
          usd: authStore.user.wallet || 0,
          eur: authStore.user.wallet_euro || 0,
          dzd: authStore.user.wallet_da || 0,
          egp: authStore.user.wallet_egp || 0,
          flexy: authStore.user.wallet_flexy || 0
        };
      }
    };

    const clearErrors = () => {
      errors.value = {};
    };

    const updateActiveWallet = async () => {
      if (isUpdatingWallet.value) return;

      isUpdatingWallet.value = true;

      try {
        const response = await api.put('/user/wallet', {
          is_da: parseInt(selectedWallet.value)
        });

        if (response.data.success) {
          // Update the auth store
          const updatedUser = { ...authStore.user, is_da: parseInt(selectedWallet.value) };
          authStore.user = updatedUser;
          dataStore.updateUserCurrency(parseInt(selectedWallet.value));



          localStorage.setItem('user', JSON.stringify(updatedUser));

          // Show success message
          Swal.fire({
            title: 'Success!',
            text: `Active wallet changed to ${getActiveWalletName()}.`,
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
            toast: true,
            position: 'top-end'
          });
        }

      } catch (error) {
        console.error('Wallet update error:', error);
        Swal.fire({
          title: 'Error!',
          text: error.response?.data?.message || 'Failed to update active wallet.',
          icon: 'error'
        });

        // Revert the selection
        selectedWallet.value = String(authStore.user?.is_da || 0);
      } finally {
        isUpdatingWallet.value = false;
      }
    };

    const updateProfile = async () => {
      if (isUpdatingProfile.value) return;

      clearErrors();
      isUpdatingProfile.value = true;

      try {
        const response = await api.put('/user/profile', {
          name: profileForm.value.name,
          phone: profileForm.value.phone,
          description: profileForm.value.description
        });

        if (response.data.success) {
          // Update the auth store with new user data
          const updatedUser = { ...authStore.user, ...response.data.user };
          authStore.user = updatedUser;
          localStorage.setItem('user', JSON.stringify(updatedUser));
          // Update original data
          originalProfileData.value = { ...profileForm.value };

          // Show success message
          Swal.fire({
            title: 'Success!',
            text: 'Profile updated successfully.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
            toast: true,
            position: 'top-end'
          });
        }

      } catch (error) {
        console.error('Profile update error:', error);

        if (error.response?.status === 422 && error.response?.data?.errors) {
          errors.value = error.response.data.errors;
        } else {
          Swal.fire({
            title: 'Error!',
            text: error.response?.data?.message || 'Failed to update profile.',
            icon: 'error'
          });
        }
      } finally {
        isUpdatingProfile.value = false;
      }
    };

    const updatePassword = async () => {
      if (isUpdatingPassword.value) return;

      // Validate password confirmation
      if (passwordForm.value.new_password !== passwordForm.value.new_password_confirmation) {
        Swal.fire({
          title: 'Error!',
          text: 'New password and confirmation do not match.',
          icon: 'error'
        });
        return;
      }

      clearErrors();
      isUpdatingPassword.value = true;

      try {
        const response = await api.put('/user/password', {
          current_password: passwordForm.value.current_password,
          password: passwordForm.value.new_password,
          password_confirmation: passwordForm.value.new_password_confirmation
        });

        if (response.data.success) {
          // Clear the form
          passwordForm.value = {
            current_password: '',
            new_password: '',
            new_password_confirmation: ''
          };

          // Show success message and redirect to login
          await Swal.fire({
            title: 'Success!',
            text: 'Password updated successfully. Please login again with your new password.',
            icon: 'success',
            confirmButtonText: 'OK'
          });

          // Logout user since token was revoked
          await authStore.logout();
          window.location.href = '/login';
        }

      } catch (error) {
        console.error('Password update error:', error);

        if (error.response?.status === 422 && error.response?.data?.errors) {
          errors.value = error.response.data.errors;
        } else {
          Swal.fire({
            title: 'Error!',
            text: error.response?.data?.message || 'Failed to update password.',
            icon: 'error'
          });
        }
      } finally {
        isUpdatingPassword.value = false;
      }
    };

    const toggleEmailNotifications = async () => {
      if (isUpdatingNotif.value || !authStore.user) return;
      const previous = notificationsEmail.value;
      notificationsEmail.value = !previous;
      isUpdatingNotif.value = true;
      try {
        const response = await updateUserPreferences({ notifications_email: notificationsEmail.value ? 1 : 0 });
        if (response && response.user) {
          authStore.user = { ...authStore.user, notifications_email: response.user.notifications_email };
          localStorage.setItem('user', JSON.stringify(authStore.user));
          dataStore.setUserData(authStore.user);
        }
      } catch (error) {
        notificationsEmail.value = previous;
        console.error('Failed to update email notification preference:', error);
        Swal.fire({ title: 'Error!', text: 'Failed to update notification preference.', icon: 'error', timer: 2000, showConfirmButton: false, toast: true, position: 'top-end' });
      } finally {
        isUpdatingNotif.value = false;
      }
    };

    const toggleRealtimeNotifications = async () => {
      if (isUpdatingNotif.value || !authStore.user) return;
      const previous = notificationsRealtime.value;
      notificationsRealtime.value = !previous;
      isUpdatingNotif.value = true;
      try {
        const response = await updateUserPreferences({ notifications_realtime: notificationsRealtime.value ? 1 : 0 });
        if (response && response.user) {
          authStore.user = { ...authStore.user, notifications_realtime: response.user.notifications_realtime };
          localStorage.setItem('user', JSON.stringify(authStore.user));
          dataStore.setUserData(authStore.user);
        }
      } catch (error) {
        notificationsRealtime.value = previous;
        console.error('Failed to update realtime notification preference:', error);
        Swal.fire({ title: 'Error!', text: 'Failed to update notification preference.', icon: 'error', timer: 2000, showConfirmButton: false, toast: true, position: 'top-end' });
      } finally {
        isUpdatingNotif.value = false;
      }
    };

    // Watch for user changes
    watch(() => authStore.user, (newUser) => {
      if (newUser) {
        loadUserData();
      }
    }, { immediate: true });

    const togglePushSub = async () => {
      if (pushSub.subscribed.value) {
        await pushSub.unsubscribeFromPush();
      } else {
        await pushSub.subscribeToPush();
      }
    };

    onMounted(() => {
      loadUserData();
      pushSub.checkSubscription();
    });

    return {
      profileForm,
      passwordForm,
      selectedWallet,
      isUpdatingWallet,
      walletBalances,
      isUpdatingProfile,
      isUpdatingPassword,
      errors,
      authStore,
      dataStore,
      userInitials,
      hasProfileChanges,
      hasPasswordChanges,
      formatCurrency,
      getActiveWalletName,
      updateActiveWallet,
      updateProfile,
      updatePassword,
      notificationsEmail,
      notificationsRealtime,
      isUpdatingNotif,
      toggleEmailNotifications,
      toggleRealtimeNotifications,
      browserNotif,
      pushSub,
      togglePushSub,
      isMobile
    };
  }
}
</script>

<style scoped>
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
:root {
  --color-background: #ffffff;
  --color-background-light: #f8fafc;
  --color-background-lighter: #f1f5f9;
  --color-text-primary: #0f172a;
  --color-text-secondary: #64748b;
  --color-primary: #3b82f6;
  --color-primary-dark: #2563eb;
}

:root.dark {
  --color-background: #0f172a;
  --color-background-light: #1e293b;
  --color-background-lighter: #334155;
  --color-text-primary: #f8fafc;
  --color-text-secondary: #cbd5e1;
  --color-primary: #60a5fa;
  --color-primary-dark: #3b82f6;
}

</style>
